/**
 * The words this product repeats everywhere, said once.
 *
 * Every entry here was found duplicated across at least two surfaces during the PRD-27
 * inventory — "Close" alone appeared in nine files. A sentence that exists twice gets
 * translated twice and then drifts, which is the whole reason this namespace exists rather
 * than each surface owning its own "Cancel".
 *
 * `en` is the source of truth AND the type (PRD-27 LOC-011): `Messages = typeof en`, so
 * every other locale is checked against the shape of this directory and a missing key is a
 * compile error rather than a runtime shrug.
 *
 * Note the explicit `: string` return annotations on functions whose body is a ternary over
 * string literals. Without them TypeScript infers a literal union and the Portuguese
 * catalogue is then unable to return its own words — it is typed into saying "just now".
 */

export const common = {
  // --- the switcher -------------------------------------------------------
  language: 'Language',
  languageNameEn: 'English',
  languageNamePt: 'Português (Brasil)',

  // --- time words the Intl formatters cannot own --------------------------
  /** Under a minute. `Intl` offers "this minute", which is not what a person says. */
  justNow: 'just now',
  expired: 'expired',
  inUnderAMinute: 'in under a minute',
  never: 'never',

  // --- chrome -------------------------------------------------------------
  close: 'Close',
  cancel: 'Cancel',
  save: 'Save',
  saving: 'Saving…',
  saved: 'Saved',
  edit: 'Edit',
  remove: 'Remove',
  delete: 'Delete',
  confirm: 'Confirm',
  back: 'Back',
  next: 'Next',
  done: 'Done',
  copy: 'Copy',
  copied: 'Copied',
  search: 'Search',
  clear: 'Clear',
  showMore: 'Show more',
  showLess: 'Show less',

  // --- states -------------------------------------------------------------
  loading: 'Loading…',
  retry: 'Try again',
  none: '—',

  /** "+3 more" on an overflow chip. `more` is a word, so it is a function, not a suffix. */
  andMore: (count: number): string => `+${count} more`,

  // --- failures the client itself produces --------------------------------
  sessionExpired: 'Your session has ended. Sign in again to continue.',
  connectionLost: 'The connection to the assistant was lost.',
  genericError: 'Something went wrong.',
  /**
   * The last resort, when a response carried no structured issue at all. The status number
   * is kept because it is the one thing a person can quote to whoever runs the deployment.
   */
  requestFailed: (status: number): string => `The request failed (${status}).`
}
